// Console & Debugging Type Definitions

export type MessageType = 'info' | 'warning' | 'error' | 'debug' | 'log' | 'table' | 'group' | 'groupEnd';

export type MessageSource = 'user' | 'system' | 'script' | 'command';

export interface StackFrame {
  functionName: string;
  fileName: string;
  lineNumber: number;
  columnNumber: number;
  source: string;
}

export interface ConsoleMessage {
  id: string;
  type: MessageType;
  source: MessageSource;
  timestamp: number;
  message: string;
  formattedMessage?: string; // For styled console output
  data?: unknown[]; // Additional console.log arguments
  stackTrace?: StackFrame[];
  count: number; // For collapsed identical messages
  isExpanded: boolean;
  groupId?: string; // For console.group
  groupLevel: number;
  fileReference?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };
}

export interface TableData {
  columns: string[];
  rows: Record<string, unknown>[];
}

export interface ConsoleGroup {
  id: string;
  label: string;
  isCollapsed: boolean;
  parentId?: string;
  level: number;
}

export interface ConsoleFilter {
  searchText: string;
  showInfo: boolean;
  showWarnings: boolean;
  showErrors: boolean;
  showDebug: boolean;
  showLogs: boolean;
  sourceFilter: MessageSource | 'all';
}

export interface ConsoleSettings {
  showTimestamps: boolean;
  showStackTraces: boolean;
  collapseIdentical: boolean;
  maxMessages: number;
  clearOnPlay: boolean;
  preserveLog: boolean;
  fontSize: number;
  wrapLines: boolean;
  showSource: boolean;
}

export interface CommandHistoryEntry {
  id: string;
  command: string;
  timestamp: number;
  result?: unknown;
  error?: string;
}

export interface ConsoleState {
  // Messages
  messages: ConsoleMessage[];
  filteredMessages: ConsoleMessage[];

  // Groups
  groups: ConsoleGroup[];
  currentGroupId: string | null;

  // Filter
  filter: ConsoleFilter;

  // Settings
  settings: ConsoleSettings;

  // Command input
  commandHistory: CommandHistoryEntry[];
  commandHistoryIndex: number;
  currentCommand: string;
  isExecuting: boolean;

  // UI State
  isScrolledToBottom: boolean;
  selectedMessageId: string | null;
  isSettingsOpen: boolean;

  // Counters
  infoCount: number;
  warningCount: number;
  errorCount: number;
  debugCount: number;
}

export interface ConsoleActions {
  // Messages
  addMessage: (type: MessageType, message: string, options?: Partial<ConsoleMessage>) => void;
  addSystemMessage: (message: string, type?: MessageType) => void;
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
  table: (data: unknown, columns?: string[]) => void;

  // Groups
  group: (label?: string) => void;
  groupCollapsed: (label?: string) => void;
  groupEnd: () => void;
  toggleGroup: (groupId: string) => void;

  // Clearing
  clear: () => void;
  clearErrors: () => void;

  // Filter
  setSearchText: (text: string) => void;
  toggleMessageType: (type: MessageType) => void;
  setSourceFilter: (source: MessageSource | 'all') => void;
  applyFilter: () => void;

  // Settings
  updateSettings: (settings: Partial<ConsoleSettings>) => void;
  toggleSettings: () => void;

  // Commands
  executeCommand: (command: string) => Promise<void>;
  setCurrentCommand: (command: string) => void;
  navigateHistory: (direction: 'up' | 'down') => void;
  clearHistory: () => void;

  // UI
  setScrolledToBottom: (value: boolean) => void;
  selectMessage: (id: string | null) => void;
  copyMessage: (id: string) => void;
  expandMessage: (id: string) => void;
  collapseMessage: (id: string) => void;
  toggleMessageExpanded: (id: string) => void;

  // Export
  exportLog: () => string;
}

// Default values
export const DEFAULT_CONSOLE_FILTER: ConsoleFilter = {
  searchText: '',
  showInfo: true,
  showWarnings: true,
  showErrors: true,
  showDebug: false,
  showLogs: true,
  sourceFilter: 'all',
};

export const DEFAULT_CONSOLE_SETTINGS: ConsoleSettings = {
  showTimestamps: true,
  showStackTraces: true,
  collapseIdentical: true,
  maxMessages: 1000,
  clearOnPlay: false,
  preserveLog: true,
  fontSize: 12,
  wrapLines: true,
  showSource: true,
};

// Message type icons and colors
export const MESSAGE_TYPE_CONFIG: Record<MessageType, { icon: string; color: string; bgColor: string }> = {
  info: { icon: 'info', color: '#9ca3af', bgColor: 'transparent' },
  warning: { icon: 'alert-triangle', color: '#eab308', bgColor: 'rgba(234, 179, 8, 0.1)' },
  error: { icon: 'x-circle', color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' },
  debug: { icon: 'bug', color: '#8b5cf6', bgColor: 'transparent' },
  log: { icon: 'file-text', color: '#d4d4d8', bgColor: 'transparent' },
  table: { icon: 'table', color: '#3b82f6', bgColor: 'transparent' },
  group: { icon: 'chevron-right', color: '#22c55e', bgColor: 'transparent' },
  groupEnd: { icon: 'chevron-right', color: '#22c55e', bgColor: 'transparent' },
};

// Max values
export const MAX_MESSAGES = 10000;
export const MAX_HISTORY_ENTRIES = 100;
export const MAX_STACK_FRAMES = 50;
